/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 * @format
 */

import { Logger } from "../../core/libs/logger";
import { SDKEventService, SDKRequestProcessers, SDKRequestIds } from "../../../../SDK/src/util/event-service";

import { UrlParameterService } from "../desktop/blast-common/url-parameter.service";
import { VCMessage } from "../../../webclient/shared/desktop/channels/rdpvcbridge/vc-client-api";
import { Injectable } from "@angular/core";
import { SDKReceivedRequest } from "../../../../SDK/src/util/event-service";
import { RDPVChanService } from "./vchan-service";
import { EventExchangeType } from "../../../../SDK/src/util/event-exchange/event-exchange";
import { VcClientAPISdk } from "../../shared/desktop/channels/rdpvcbridge/vc-client/vc-client-api-sdk";
import { clientUtil } from "@html-core";

class SDKBlastOptions {
   public sessionKey: string;
   public uuid: string = "";
   public channelOptions = "";
   public parentAppId = "";
   public workingVersion: number = -1;
   constructor(urlParameterService: UrlParameterService) {
      if (
         !urlParameterService.sdkOptions ||
         !urlParameterService.sdkOptions.parentAppId ||
         !urlParameterService.sdkOptions.uuid ||
         !urlParameterService.sdkOptions.workingVersion
      ) {
         Logger.info(
            "skip vchan due to missing sdk option" + JSON.stringify(urlParameterService.sdkOptions),
            Logger.RDPVCBRIDGE
         );
         return;
      }
      Logger.info("init the vchan for this session by: " + urlParameterService.sessionKey, Logger.RDPVCBRIDGE);
      this.sessionKey = urlParameterService.sessionKey;
      this.channelOptions = urlParameterService.sdkOptions.channelOptions;
      this.parentAppId = urlParameterService.sdkOptions.parentAppId;
      this.uuid = urlParameterService.sdkOptions.uuid;
      this.workingVersion = urlParameterService.sdkOptions.workingVersion;
   }
}

@Injectable()
export class SDKBlastService {
   private parentAppId: string;
   private eventService: SDKEventService;
   private processerType: string;
   public sdkOptions: SDKBlastOptions;
   private sessionKey: string;
   private logger = new Logger(Logger.SDK_BLAST);
   constructor(
      private vcClientAPI: VcClientAPISdk,
      private rdpVChanService: RDPVChanService
   ) {
      this.rdpVChanService.init();
   }

   public init = (urlParameterService: UrlParameterService) => {
      this.sdkOptions = new SDKBlastOptions(urlParameterService);
      this.parentAppId = this.sdkOptions.parentAppId;
      const validOrigins = ["chrome-extension://" + this.parentAppId];
      this.processerType = SDKRequestProcessers.blast + this.sdkOptions.uuid;
      const workingVersion = this.sdkOptions.workingVersion;

      if (clientUtil.isIwaClient()) {
         if (workingVersion <= 2) {
            this.logger.error("SDK is too old to control IWA, please upgrade to version 3+");
            return;
         }
         this.eventService = new SDKEventService(EventExchangeType.LongLived, validOrigins, true);
      } else {
         this.eventService = new SDKEventService(
            workingVersion >= 2 ? EventExchangeType.LongLived : EventExchangeType.OneTime,
            validOrigins
         );
      }
      this.logger.info("init sdk blast service, working version: " + workingVersion);
      this.sessionKey = urlParameterService.sessionKey;
      this.logger.info("init sdk blast service with vchan option " + this.sdkOptions.channelOptions);
      this.vcClientAPI.init(this.sdkOptions.uuid, this.sessionKey, this.sdkOptions.channelOptions, this.sendEvent);
      this.eventService.addListener(validOrigins[0], this.processerType, this.onMessage);
   };

   public sendEvent = async (eventType: SDKRequestIds, data: any) => {
      const eventData = {
         name: eventType,
         param: data
      };
      try {
         await this.eventService.sendMessage(this.parentAppId, this.processerType, eventData, null);
      } catch (e) {
         this.logger.exception(e);
      }
   };
   public onMessage = (event: SDKReceivedRequest) => {
      if (!event || !event.request || !event.request.isRequest) {
         return;
      }
      if (event.peerAddress! == this.parentAppId) {
         return;
      }
      const requestData = event.request.data;
      if (!requestData || !requestData.name || !requestData.param) {
         this.logger.warning("invalid requestFormat");
         return;
      }
      switch (requestData.name) {
         case SDKRequestIds.VchanToRemoteData: {
            const vcMessage: VCMessage = requestData.param;
            this.vcClientAPI.sendOnVchan(vcMessage); //Could return status code if needed
            event.sendResponse(null);
            break;
         }
         default: {
            this.logger.warning("unknown request detected in vchan module");
         }
      }
   };
}
